import type { User } from '@/modules/user/userTypes';
import service, { type HttpResponseSuccess } from './service';

const apiService = service(`${process.env.REACT_BACKEND_HOST}`);

/**
 * Represents the parameters for getting a Nylas auth URL.
 */
interface GetNylasAuthUrlParams {
	email: string;
	scopes: string[];
}

/**
 * Represents the response data for the Nylas auth URL.
 */
interface NylasAuthUrlResponse {
	authUrl: string;
}

/**
 * Represents the parameters for exchanging a Nylas code.
 */
interface ExchangeNylasCodeParams {
	code: string;
}

/**
 * Represents the response data for exchanging a Nylas code.
 */
interface ExchangeNylasCodeResponse {
	success: boolean;
	message: string;
}

interface Company {
	id: string;
	companyHsId: string;
	name: string;
}

interface AdminCreateUsersResponse {
	data: string; // "Successfully created accounts"
}

interface CreateUser {
	email: string;
	name: string;
	password: string;
	role: string;
}

interface MarketCoordinate {
	lat: string;
	lng: string;
	locationName: string;
	streetAddress: string;
	cityName: string;
	stateName: string;
	zipcode: string;
	radius: string;
	marketType: string;
}

interface AdminCreateUsersPayload {
	createUsers: CreateUser[];
	locationCoordinates: MarketCoordinate[];
	company: Company;
	companyHsId: string;
}

export const login = (
	email: string,
	password: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/sign-in', {
		email,
		password
	});

export const getUser = (): Promise<HttpResponseSuccess<User>> => {
	if (sessionStorage.admin_token) {
		return apiService.post('/api/user/getUser', { adminAccess: true });
	}

	return apiService.post('/api/user/getUser');
};

export const getUserByEmail = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/getUserByEmail', payload);

export const adminCreateUsers = (
	payload: AdminCreateUsersPayload
): Promise<HttpResponseSuccess<AdminCreateUsersResponse>> => {
	return apiService.post('/api/user/adminCreateAccounts', payload);
};
export const getUsers = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/user/getUsers');

export const resetPassword = (
	oldPassword: string,
	password: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/reset/password', {
		oldPassword,
		password
	});

export const adminResetPassword = (
	email: string,
	password: string,
	confirmPassword: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/adminResetPassword', {
		email,
		password,
		confirmPassword
	});

export const getNylasStateToken = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/getNylasStateToken');

export const updateUserEmailsLimit = (): Promise<
	HttpResponseSuccess<unknown>
> => apiService.post('/api/user/updateEmailsLimit');

export const updateUserAdmin = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/updateUserAdmin', payload);

export const userAddEmailToken = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/exchangeNylasCode', payload);

export const updateSubscriptions = (
	payload: any,
	token: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/subscriptions', payload, {
		headers: {
			Authorization: token
		}
	});

export const userAddNewUserRequest = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/outbound-email/sendAddNewUserEmail', payload);

export const userDeleteUserRequest = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/deleteUser', payload);

export const addHostedEmail = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/assignRe2HostedEmail', payload);

export const userAddNewBccRequest = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/addNewBccEmail', payload);

export const userDeleteBccRequest = () =>
	apiService.post('/api/user/deleteBccEmail');

export const userCreateScheduleRequest = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/createScheduleRequest', payload);

export const adminAccessAccountRequest = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/adminAccessAccount', payload);

export const userLogoutUser = (payload: any) =>
	apiService.post('/api/user/logout', payload);

export const userGetNewCommunications = () =>
	apiService.post('/api/note/getUnreadMessages');

export const userGetNotifications = () =>
	apiService.post('/api/note/getNotifications');

export const userPostNotificationsRead = () =>
	apiService.post('/api/note/notificationsMarkAsRead');

export const checkUserExists = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/checkUserExists', payload);

export const verifySignupEmail = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/verifySignupEmail', payload);

export const signUpFacebookFreemium = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/signUpFacebookFreemium', payload);

interface GenerateOtpSecretResponse {
	otpAuthUrl: string;
	otpBase32Secret: string;
}

export const generateOtpSecret = (): Promise<
	HttpResponseSuccess<GenerateOtpSecretResponse>
> => apiService.post('/api/user/generateOtpSecret');

export const enableOtp = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/enableOtp', payload);

export const deactivateOtp = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/deactivateOtp');

export const verifyOtp = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/verifyOtp', payload);

export const resetOtp = (payload: any): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/resetOtp', payload);

/**
 * Updates the email sending method for a user.
 *
 * @param {string} userEmail - The email address of the user.
 * @param {string} emailSystem - The email system to be used.
 * @returns {Promise<HttpResponseSuccess<unknown>>} A promise that resolves to the API response.
 */
export const updateEmailSendingMethod = (
	userEmail: string,
	emailSystem: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/updateEmailSendingMethod', {
		userEmail,
		emailSystem
	});

export const userUpdateActiveStatus = (
	userEmail: string,
	action: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/user/setUserActiveStatus', {
		userEmail,
		action
	});

// Nylas Auth

/**
 * Requests a Nylas authentication URL.
 * @param {GetNylasAuthUrlParams} params - The parameters for getting the auth URL.
 * @returns {Promise<HttpResponseSuccess<NylasAuthUrlResponse promise that resolves with the auth URL.
 */
export const getNylasAuthUrl = (
	params: GetNylasAuthUrlParams
): Promise<HttpResponseSuccess<NylasAuthUrlResponse>> =>
	apiService.post('/api/user/getNylasAuthUrl', params);

/**
 * Exchanges a Nylas authentication code for a token.
 * @param {ExchangeNylasCodeParams} params - The parameters for exchanging the code.
 * @returns {Promise<HttpResponseSuccess<ExchangeNylasCodeResponse promise that resolves with the exchange result.
 */
export const exchangeNylasCode = (
	params: ExchangeNylasCodeParams
): Promise<HttpResponseSuccess<ExchangeNylasCodeResponse>> =>
	apiService.post('/api/user/exchangeNylasCode', params);
